import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  AlertCircle,
  Building2,
  Check,
  KeyRound,
  Loader2,
  Mail,
  Info,
  Plus,
  Shapes,
  ShieldCheck,
  Sparkles,
  Tag,
  Trash2,
  Truck,
} from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";
import { getDefaultWorkspaceRoute } from "@/lib/permissions";
import AuthLanguageSelect from "@/components/auth/AuthLanguageSelect";
import AuthField from "@/components/auth/AuthField";
import AuthInput from "@/components/auth/AuthInput";
import AuthOtpInput from "@/components/auth/AuthOtpInput";
import PhoneNumberField from "@/components/auth/PhoneNumberField";
import FormSelect from "@/components/ui/form-select";
import { toast } from "sonner";
import { useLocalization } from "@/lib/localization";
import {
  checkAuthEmail,
  loginWithPassword,
  registerShop,
  resetLoginPassword,
  setupStaffPassword,
  startAuthOTP,
  verifyAuthOTP,
} from "@/lib/authApi";
import { useAuth } from "@/lib/store";
import { AFRICAN_COUNTRIES } from "@/features/auth/independent/africanCountries";

const REGISTRATION_EXTRAS = {
  ar: { days: 'أيام', referral: 'رمز الإحالة (اختياري)', referralPlaceholder: 'مثال: SHOP7K2LM' },
  en: { days: 'days', referral: 'Referral code (optional)', referralPlaceholder: 'e.g. SHOP7K2LM' },
  fr: { days: 'jours', referral: 'Code de parrainage (facultatif)', referralPlaceholder: 'ex. SHOP7K2LM' },
  ha: { days: 'kwanaki', referral: 'Lambar gayyata (ba dole ba)', referralPlaceholder: 'misali SHOP7K2LM' },
  ig: { days: 'ụbọchị', referral: 'Koodu nrụtụ aka (nhọrọ)', referralPlaceholder: 'dịka SHOP7K2LM' },
  pt: { days: 'dias', referral: 'Código de referência (opcional)', referralPlaceholder: 'ex. SHOP7K2LM' },
  sw: { days: 'siku', referral: 'Msimbo wa marejeleo (si lazima)', referralPlaceholder: 'mf. SHOP7K2LM' },
  yo: { days: 'ọjọ́', referral: 'Kóòdù ìtọ́kasí (àṣàyàn)', referralPlaceholder: 'àpẹẹrẹ SHOP7K2LM' },
  zu: { days: 'izinsuku', referral: 'Ikhodi yenkomba (uyazikhethela)', referralPlaceholder: 'isb. SHOP7K2LM' },
};

const ACCOUNT_NOT_FOUND_ERROR = "no shop account found for this email";
function normalizeDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

function normalizeEmail(email) {
  return String(email || "").trim().toLowerCase();
}

function normalizePhone(phone, dialCode) {
  const digits = normalizeDigits(phone);
  const dialDigits = normalizeDigits(dialCode);
  if (digits.startsWith(dialDigits)) {
    return `+${digits}`;
  }
  return `${dialCode}${digits}`.trim();
}

function normalizePin(value) {
  return normalizeDigits(value).slice(0, 4);
}

const PASSWORD_PATTERN = /^(?=.*\p{L})(?=.*\p{N}).{8,72}$/u;
const PASSWORD_HELP = "Use 8–72 characters with at least one letter and one number.";

function normalizeLeadTime(value) {
  const digits = normalizeDigits(value).slice(0, 3);
  if (!digits) return "";
  return digits.replace(/^0+/, "") || "1";
}

function leadTimeCopy(days, messages) {
  return messages.auth.screen.supplierLeadTime(days);
}

function formatRoleLabel(role, messages) {
  const normalized = role === "Owner" ? "Admin" : role;
  const copy = messages.branchesPage;
  const map = {
    Admin: copy.roleAdmin,
    Member: copy.roleMember,
    Owner: copy.roleOwner,
  };
  return map[normalized] ?? normalized ?? role;
}

function createShopSetupSchema(messages) {
  return z
    .object({
      confirmPin: z.string(),
      leadTimeDays: z.string().trim().min(1, messages.auth.validation.leadTimeRequired).regex(/^\d+$/, messages.auth.validation.invalidLeadTimeWhole),
      phone: z.string().refine((value) => normalizeDigits(value).length >= 7, "Enter a valid business phone number."),
      pin: z.string().regex(PASSWORD_PATTERN, PASSWORD_HELP),
      shopName: z.string().trim().min(2, messages.auth.validation.shopNameRequired).max(80, messages.auth.validation.shopNameTooLong),
    })
    .superRefine((values, context) => {
      if (values.pin !== values.confirmPin) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Passwords do not match.",
          path: ["confirmPin"],
        });
      }

      const leadTimeDays = Number(values.leadTimeDays);
      if (!Number.isInteger(leadTimeDays) || leadTimeDays < 1) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: messages.auth.validation.leadTimeOneOrMore,
          path: ["leadTimeDays"],
        });
      }
    });
}

function createBranchSetupSchema(existingBranches, messages) {
  return z
    .object({
      branchName: z.string().trim(),
      confirmPin: z.string(),
      leadTimeDays: z.string().trim().min(1, messages.auth.validation.leadTimeRequired).regex(/^\d+$/, messages.auth.validation.invalidLeadTimeWhole),
      phone: z.string().refine((value) => normalizeDigits(value).length >= 7, "Enter a valid business phone number."),
      pin: z.string().regex(PASSWORD_PATTERN, PASSWORD_HELP),
      shopName: z.string().trim().min(2, messages.auth.validation.shopNameRequired).max(80, messages.auth.validation.shopNameTooLong),
    })
    .superRefine((values, context) => {
      if (values.pin !== values.confirmPin) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Passwords do not match.",
          path: ["confirmPin"],
        });
      }

      const leadTimeDays = Number(values.leadTimeDays);
      if (!Number.isInteger(leadTimeDays) || leadTimeDays < 1) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: messages.auth.validation.leadTimeOneOrMore,
          path: ["leadTimeDays"],
        });
      }

      if (!values.branchName.trim() && existingBranches.length === 0) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: messages.auth.validation.addBranchToContinue,
          path: ["branchName"],
        });
      }

      if (
        values.branchName.trim() &&
        existingBranches.some((branch) => branch.name.toLowerCase() === values.branchName.trim().toLowerCase())
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: messages.auth.validation.branchDuplicate,
          path: ["branchName"],
        });
      }
    });
}

function createRecoverPinSchema(messages) {
  return z
    .object({
      confirmPin: z.string(),
      pin: z.string().regex(PASSWORD_PATTERN, PASSWORD_HELP),
    })
    .superRefine((values, context) => {
      if (values.pin !== values.confirmPin) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Passwords do not match.",
          path: ["confirmPin"],
        });
      }
    });
}

function mapZodErrors(error) {
  const nextErrors = {};
  for (const issue of error.issues) {
    const field = issue.path[0];
    if (typeof field === "string" && !nextErrors[field]) {
      nextErrors[field] = issue.message;
    }
  }
  return nextErrors;
}

function resolveCountryFromBrowser() {
  if (typeof window === "undefined") {
    return AFRICAN_COUNTRIES.find((country) => country.code === "NG") || AFRICAN_COUNTRIES[0];
  }

  const localeCandidates = [
    ...(navigator.languages || []),
    navigator.language,
    Intl.DateTimeFormat().resolvedOptions().locale,
  ].filter(Boolean);

  for (const locale of localeCandidates) {
    const match = String(locale).match(/[-_](?<region>[A-Z]{2})$/i);
    const region = match?.groups?.region?.toUpperCase();
    const found = AFRICAN_COUNTRIES.find((country) => country.code === region);
    if (found) {
      return found;
    }
  }

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneCountryMap = {
    "Africa/Algiers": "DZ",
    "Africa/Cairo": "EG",
    "Africa/Casablanca": "MA",
    "Africa/Johannesburg": "ZA",
    "Africa/Lagos": "NG",
    "Africa/Nairobi": "KE",
    "Africa/Tunis": "TN",
  };
  const fallbackCode = timeZoneCountryMap[timeZone];

  return AFRICAN_COUNTRIES.find((country) => country.code === fallbackCode)
    || AFRICAN_COUNTRIES.find((country) => country.code === "NG")
    || AFRICAN_COUNTRIES[0];
}

export default function IndependentAuth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signInIndependent } = useAuth();
  const { isRTL, messages } = useLocalization();
  const initialMode = ["login", "register", "recover"].includes(searchParams.get("mode"))
    ? searchParams.get("mode")
    : "login";
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState("email");
  const [country, setCountry] = useState(() => resolveCountryFromBrowser());
  const [detectedCountryCode, setDetectedCountryCode] = useState(() => resolveCountryFromBrowser().code);
  const [countrySelectionSource, setCountrySelectionSource] = useState("browser_region");
  const countrySelectionTouched = useRef(false);
  const [email, setEmail] = useState(() => normalizeEmail(searchParams.get("email") || ""));
  const [industry, setIndustry] = useState("");
  const [normalizedEmail, setNormalizedEmail] = useState("");
  const [actorType, setActorType] = useState(null);
  const [loginStep, setLoginStep] = useState(null);
  const [code, setCode] = useState("");
  const [resendCount, setResendCount] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  const [resumeRegistrationAtBranches, setResumeRegistrationAtBranches] = useState(false);
  const [staffShopOptions, setStaffShopOptions] = useState([]);
  const [selectedStaffShopId, setSelectedStaffShopId] = useState("");
  const [pendingDevicePIN, setPendingDevicePIN] = useState("");
  const [shopName, setShopName] = useState("");
  const [referralCode, setReferralCode] = useState(() => searchParams.get("ref")?.trim().toUpperCase() || "");
  const [isReferralVisible, setIsReferralVisible] = useState(() => Boolean(searchParams.get("ref")?.trim()));
  const [branchName, setBranchName] = useState("");
  const [branches, setBranches] = useState([]);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [leadTimeDays, setLeadTimeDays] = useState("1");
  const [phone, setPhone] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [formError, setFormError] = useState("");

  const copy = messages.auth.copy[mode];
  const industryOptions = messages.auth.industryOptions;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(email));
  const needsIndustry = mode === "register";
  const registrationPasswordValid = PASSWORD_PATTERN.test(pin) && pin === confirmPin;
  const canSubmitEmail = emailValid && (
    mode === "login"
      ? pin.length >= 8
      : !needsIndustry || (industry && registrationPasswordValid)
  );
  const isLoginPIN = mode === "login" && loginStep === "password_login";
  const cleanCode = isLoginPIN ? code : normalizePin(code);
  const isDeviceVerification = mode === "login" && loginStep === "device_replace";
  const registrationStepIndexes = {
    email: 1,
    verify: 2,
    shopSetup: 3,
    branchSetup: 4,
  };
  const stepCount = mode === "register" ? 4 : 3;
  const stepIndex = mode === "register"
    ? registrationStepIndexes[step] || 3
    : step === "email" ? 1 : step === "verify" ? 2 : 3;
  useEffect(() => {
    const detected = resolveCountryFromBrowser();
    setDetectedCountryCode(detected.code);
    if (!countrySelectionTouched.current) setCountry(detected);
  }, []);

  function resetVerificationState(nextMode = mode) {
    setStep("email");
    setMode(nextMode);
    setCode("");
    setError("");
    setShowRegisterPrompt(false);
    setResumeRegistrationAtBranches(false);
    setStaffShopOptions([]);
    setSelectedStaffShopId("");
    setPendingDevicePIN("");
    setPin("");
    setConfirmPin("");
    setFormErrors({});
    setFormError("");
  }

  function completeAuth(result, destination) {
    signInIndependent({
      ...result.session,
      expiresAt: result.expiresAt,
      sessionId: result.sessionId,
    });
    navigate(
      destination || getDefaultWorkspaceRoute(result.session),
    );
  }

  async function submitEmail(event) {
    event?.preventDefault();
    if (busy) return;
    if (!canSubmitEmail) {
      setError(
        !emailValid
          ? "Enter a valid email address."
          : mode === "login" && pin.length < 8
            ? "Enter your password to continue."
          : needsIndustry && !industry
            ? messages.auth.validation.needIndustry
            : !PASSWORD_PATTERN.test(pin)
              ? PASSWORD_HELP
              : "Passwords do not match.",
      );
      return;
    }

    const nextEmail = normalizeEmail(email);
    setNormalizedEmail(nextEmail);
    setBusy(true);
    setError("");
    setShowRegisterPrompt(false);

    try {
      let access = null;
      if (mode !== "register") {
        access = await checkAuthEmail({ mode, email: nextEmail });
        setActorType(access.actorType);

		if (access.nextStep === "staff_shop_lookup" && access.shops?.length) {
			setStaffShopOptions(access.shops);
			setSelectedStaffShopId(access.shops[0]?.shopId ?? "");
			if (access.shops.length === 1) {
				await continueWithStaffShop(access.shops[0]);
			} else {
				setStep("staffShop");
			}
			return;
		}

        if (mode === "login" && access.nextStep === "password_login") {
          await performPasswordLogin(nextEmail, pin);
          return;
        }

        if (mode === "login" && access.nextStep === "otp_setup") {
          await startAuthOTP({ mode, email: nextEmail });
          setLoginStep(access.nextStep);
          setStep("verify");
          return;
        }
      }

      if (mode !== "login") {
        await startAuthOTP({ mode, email: nextEmail });
      }

      setLoginStep(undefined);
      setStep("verify");
    } catch (apiError) {
      if (mode === "register" && apiError instanceof Error && apiError.message.toLowerCase() === "email already registered") {
        setError("This email already has a Shopbook account. Sign in instead.");
        return;
      }
      setError(apiError instanceof Error ? apiError.message : messages.auth.feedback.continueFailed);
    } finally {
      setBusy(false);
    }
  }

  async function performPasswordLogin(loginEmail, password) {
    const result = await loginWithPassword({ email: loginEmail, password });
    if (result.status === "device_verification_required") {
      setNormalizedEmail(loginEmail);
      setPendingDevicePIN(password);
      setLoginStep("device_replace");
      setCode("");
      setStep("verify");
      toast.info("We sent a verification code to your personal email.");
      return;
    }
    completeAuth(result);
  }

  async function verifyCode() {
    if (busy) return;
    if (isLoginPIN ? cleanCode.length < 8 : cleanCode.length < 4) {
      setError(isLoginPIN ? "Enter your password to continue." : messages.auth.verification.enterCodeFallback);
      return;
    }

    setBusy(true);
    setError("");
    setShowRegisterPrompt(false);

    try {
      if (isLoginPIN) {
        const result = await loginWithPassword({
          email: normalizedEmail,
          password: cleanCode,
        });
		if (result.status === "device_verification_required") {
			setPendingDevicePIN(cleanCode);
			setLoginStep("device_replace");
			setCode("");
			toast.info("We sent a verification code to your personal email.");
			return;
		}
        completeAuth(result);
        return;
      }

	  if (isDeviceVerification) {
		await verifyAuthOTP({ code: cleanCode, email: normalizedEmail, mode: "device_replace" });
		const result = await loginWithPassword({
		  email: normalizedEmail,
		  password: pendingDevicePIN,
		});
		completeAuth(result);
		return;
	  }

      await verifyAuthOTP({
        code: cleanCode,
        mode,
        email: normalizedEmail,
      });

      if (loginStep === "otp_setup") {
        setStep("staffPinSetup");
        return;
      }
      if (mode === "register") {
        setStep(resumeRegistrationAtBranches ? "branchSetup" : "shopSetup");
        setResumeRegistrationAtBranches(false);
        return;
      }
      setStep("recoverPin");
    } catch (apiError) {
      if (
        mode === "login" &&
        apiError instanceof Error &&
        apiError.message.toLowerCase() === ACCOUNT_NOT_FOUND_ERROR
      ) {
        setShowRegisterPrompt(true);
        setError(messages.auth.feedback.noAccountFound);
        return;
      }
      setError(apiError instanceof Error ? apiError.message : messages.auth.feedback.verifyCodeFailed);
    } finally {
      setBusy(false);
    }
  }

  async function resendCode() {
    if (isLoginPIN || busy) return;
    setBusy(true);
    try {
      await startAuthOTP({ mode: isDeviceVerification ? "device_replace" : mode, email: normalizedEmail });
      setResendCount((count) => count + 1);
      setError("");
      toast.success(messages.auth.feedback.codeSent);
    } catch (apiError) {
      setError(apiError instanceof Error ? apiError.message : messages.auth.feedback.resendFailed);
    } finally {
      setBusy(false);
    }
  }

  async function continueWithStaffShop(option) {
	const loginEmail = normalizeEmail(option?.loginEmail || "");
	if (!loginEmail) return;
	setEmail(loginEmail);
	setNormalizedEmail(loginEmail);
	setActorType("staff");
	setLoginStep(option.status === "Invited" ? "otp_setup" : mode === "recover" ? "otp_recover" : "password_login");
	setCode("");
	if (option.status === "Invited" || mode === "recover") {
	  await startAuthOTP({ mode: mode === "recover" ? "recover" : "login", email: loginEmail });
	}
	setStaffShopOptions([]);
	if (option.status === "Active" && mode === "login") {
	  await performPasswordLogin(loginEmail, pin);
	  return;
	}
	setStep("verify");
  }

  async function selectStaffShop() {
    if (!selectedStaffShopId || busy) return;
    setBusy(true);
    setError("");
    try {
	  const option = staffShopOptions.find((item) => item.shopId === selectedStaffShopId);
	  await continueWithStaffShop(option);
    } catch (apiError) {
      setError(apiError instanceof Error ? apiError.message : messages.auth.feedback.openShopFailed);
    } finally {
      setBusy(false);
    }
  }

  function clearFieldError(field) {
    setFormErrors((current) => ({ ...current, [field]: undefined }));
    setFormError("");
  }

  function addBranch() {
    const name = branchName.trim();
    if (!name) {
      setFormErrors((current) => ({ ...current, branchName: messages.auth.validation.branchRequired }));
      return;
    }
    if (name.length < 2) {
      setFormErrors((current) => ({ ...current, branchName: messages.auth.validation.branchTooShort }));
      return;
    }
    if (branches.some((branch) => branch.name.toLowerCase() === name.toLowerCase())) {
      setFormErrors((current) => ({ ...current, branchName: messages.auth.validation.branchDuplicate }));
      return;
    }

    setBranches((current) => [...current, { id: `branch-${Date.now()}-${current.length + 1}`, name }]);
    setBranchName("");
    clearFieldError("branchName");
  }

  function continueToBranchSetup() {
    const result = createShopSetupSchema(messages).safeParse({
      confirmPin,
      leadTimeDays,
      phone,
      pin,
      shopName,
    });
    if (!result.success) {
      setFormErrors(mapZodErrors(result.error));
      setFormError("");
      return;
    }

    setShopName(result.data.shopName.trim());
    setLeadTimeDays(result.data.leadTimeDays.trim());
    setPhone(normalizePhone(result.data.phone, country.dial));
    setPin(result.data.pin);
    setConfirmPin(result.data.confirmPin.trim());
    setFormErrors({});
    setFormError("");
    setStep("branchSetup");
  }

  async function completeBranchSetup() {
    const result = createBranchSetupSchema(branches, messages).safeParse({
      branchName,
      confirmPin,
      leadTimeDays,
      phone,
      pin,
      shopName,
    });
    if (!result.success) {
      setFormErrors(mapZodErrors(result.error));
      setFormError("");
      return;
    }

    const pendingBranchName = result.data.branchName.trim();
    const branchesToSubmit = pendingBranchName
      ? [...branches, { id: `branch-${Date.now()}-${branches.length + 1}`, name: pendingBranchName }]
      : branches;

    setBusy(true);
    setFormErrors({});
    setFormError("");
    try {
      const leadDays = Number(result.data.leadTimeDays.trim());
      const authResult = await registerShop({
        billingCountryCode: country.code,
        countrySelectionSource,
        detectedCountryCode,
        branchNames: branchesToSubmit.map((branch) => branch.name.trim()),
        leadTimeDays: leadDays,
        leadTimeLabel: leadTimeCopy(leadDays, messages),
        email: normalizedEmail,
        phone: normalizePhone(result.data.phone, country.dial),
        password: result.data.pin,
        shopName: result.data.shopName.trim(),
        referralCode: referralCode.trim().toUpperCase() || undefined,
      });
      // Registration already provisions the three-day trial. Enter the
      // workspace normally; subscription remains available from navigation.
      completeAuth(authResult);
    } catch (apiError) {
      if (
        apiError instanceof Error &&
        apiError.message.trim().toLowerCase() === "verification required"
      ) {
        try {
          await startAuthOTP({ mode: "register", email: normalizedEmail });
          setCode("");
          setResumeRegistrationAtBranches(true);
          setStep("verify");
          setError("Your verification expired. Enter the new code we sent.");
          return;
        } catch (verificationError) {
          setFormError(
            verificationError instanceof Error
              ? verificationError.message
              : messages.auth.feedback.resendFailed,
          );
          return;
        }
      }
      setFormError(apiError instanceof Error ? apiError.message : messages.auth.feedback.completeRegistration);
    } finally {
      setBusy(false);
    }
  }

  async function completeRecoverPINReset() {
    const result = createRecoverPinSchema(messages).safeParse({ confirmPin, pin });
    if (!result.success) {
      setFormErrors(mapZodErrors(result.error));
      setFormError(messages.auth.feedback.reviewPinFields);
      return;
    }

    setBusy(true);
    setFormErrors({});
    setFormError("");
    try {
      const authResult = await resetLoginPassword({
        code: cleanCode,
        email: normalizedEmail,
        password: result.data.pin,
      });
      completeAuth(authResult);
    } catch (apiError) {
      setFormError(apiError instanceof Error ? apiError.message : messages.auth.feedback.resetPinFailed);
    } finally {
      setBusy(false);
    }
  }

  async function completeStaffPINSetup() {
    const result = createRecoverPinSchema(messages).safeParse({ confirmPin, pin });
    if (!result.success) {
      setFormErrors(mapZodErrors(result.error));
      setFormError(messages.auth.feedback.reviewPinFields);
      return;
    }

    setBusy(true);
    setFormErrors({});
    setFormError("");
    try {
      const authResult = await setupStaffPassword({
        email: normalizedEmail,
        password: result.data.pin,
      });
      completeAuth(authResult);
    } catch (apiError) {
      setFormError(apiError instanceof Error ? apiError.message : messages.auth.feedback.finishStaffSetup);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="grid min-h-[100dvh] w-full grid-cols-1 lg:grid-cols-[1fr_1fr]">
      <aside className="auth-brand-panel teal-mesh relative hidden overflow-hidden p-8 text-white lg:sticky lg:top-0 lg:flex lg:h-[100dvh] lg:flex-col lg:justify-between xl:p-10">
        <div className="absolute inset-0 bg-dot opacity-[0.06] pointer-events-none" />
        <Logo size={42} auth tagline inverse className="relative" />

        <div className="auth-brand-summary relative max-w-md">
          <div className="inline-flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.24em] text-white/70 mb-6">
            {messages.auth.panel.badge}
          </div>
          <h1 className="font-editorial italic font-light text-4xl xl:text-5xl leading-[1.02] text-balance text-white">
            {messages.auth.panel.titleLead}{" "}
            <span className="not-italic font-medium">{messages.auth.panel.titleTail}</span>
          </h1>
          <p className="auth-brand-subtitle mt-5 text-sm leading-[1.6] text-white/70">
            {messages.auth.panel.subtitle}
          </p>

          <div className="auth-brand-cards mt-8 grid max-w-sm grid-cols-2 gap-4">
            <Card k="Email" l="Your account key" />
            <Card k="Password + OTP" l="Secure sign-in" />
            <Card k={messages.auth.panel.cards.branches.title} l={messages.auth.panel.cards.branches.label} />
            <Card k={messages.auth.panel.cards.staff.title} l={messages.auth.panel.cards.staff.label} />
          </div>
        </div>

        <div className="relative flex items-center gap-2 text-xs text-white/50">
          <ShieldCheck className="h-3.5 w-3.5" />
          <span>{messages.auth.panel.footer}</span>
        </div>
      </aside>

      <main className="flex min-h-[100dvh] min-w-0 flex-col justify-center px-4 py-5 sm:px-8 sm:py-6 lg:px-10">
        <div className="auth-panel-content mx-auto w-full max-w-lg">
          <div className="mb-4 flex min-w-0 items-center justify-between gap-3 sm:mb-5 sm:gap-4">
            <button
              data-testid="auth-independent-back"
              onClick={() => {
                if (step === "email") return;
                if (step === "branchSetup") {
                  setStep("shopSetup");
                  return;
                }
                if (["shopSetup", "recoverPin", "staffPinSetup", "staffShop"].includes(step)) {
                  setStep("verify");
                  return;
                }
                resetVerificationState(mode);
              }}
              className="inline-flex min-w-0 items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> {messages.common.back}
            </button>
            <AuthLanguageSelect />
          </div>

          <div className="mb-5 sm:mb-6 lg:hidden">
            <Logo size={42} auth tagline />
          </div>

          <div className="mb-5 flex min-w-0 items-center gap-1.5 sm:mb-6 sm:gap-2">
            {Array.from({ length: stepCount }, (_, index) => (
              <div
                key={index}
                className={`h-1.5 w-6 shrink-0 rounded-full transition-colors sm:w-8 ${
                  index < stepIndex ? "bg-primary" : "bg-border"
                }`}
              />
            ))}
            <span className="ml-1.5 min-w-0 truncate text-[11px] uppercase tracking-wider text-muted-foreground sm:ml-2 sm:text-xs">
              {messages.common.step(Math.min(stepIndex, stepCount), stepCount)}
            </span>
          </div>

          {step === "email" ? (
            <EmailStep
              busy={busy}
              canSubmitEmail={canSubmitEmail}
              copy={copy}
              error={error}
              industry={industry}
              industryOptions={industryOptions}
              mode={mode}
              needsIndustry={needsIndustry}
              messages={messages}
              onIndustryChange={setIndustry}
              onModeChange={(nextMode) => {
                resetVerificationState(nextMode);
              }}
              onEmailChange={(value) => {
                setEmail(value);
                setError("");
              }}
              onSubmit={submitEmail}
              email={email}
              password={pin}
              confirmPassword={confirmPin}
              onPasswordChange={(value) => {
                setPin(value.slice(0, 72));
                setError("");
              }}
              onConfirmPasswordChange={(value) => {
                setConfirmPin(value.slice(0, 72));
                setError("");
              }}
            />
          ) : step === "verify" ? (
            <VerifyStep
              busy={busy}
              code={code}
              error={error}
              isLoginPIN={isLoginPIN}
              loginStep={loginStep}
              messages={messages}
              mode={mode}
              normalizedEmail={normalizedEmail}
              onCodeChange={(value) => {
                setCode(isLoginPIN ? value.slice(0, 72) : normalizePin(value));
                setError("");
                setShowRegisterPrompt(false);
              }}
              onRegisterInstead={() => {
                setEmail(email);
                resetVerificationState("register");
              }}
              onResend={resendCode}
              onSubmit={verifyCode}
              resendCount={resendCount}
              showRegisterPrompt={showRegisterPrompt}
            />
          ) : step === "staffShop" ? (
            <StaffShopStep
              busy={busy}
              error={error}
              onSelect={setSelectedStaffShopId}
              onSubmit={selectStaffShop}
              options={staffShopOptions}
              email={normalizedEmail}
              selectedShopId={selectedStaffShopId}
              messages={messages}
            />
          ) : step === "staffPinSetup" ? (
            <PinSetupStep
              busy={busy}
              confirmPin={confirmPin}
              errors={formErrors}
              formError={formError}
              messages={messages}
              onConfirmPinChange={(value) => {
                setConfirmPin(value.slice(0, 72));
                clearFieldError("confirmPin");
              }}
              onPinChange={(value) => {
                setPin(value.slice(0, 72));
                clearFieldError("pin");
              }}
              onSubmit={completeStaffPINSetup}
              pin={pin}
              subtitle="Create a password for future staff sign-ins."
              title="Set your password"
              submitLabel={messages.auth.screen.activateStaffAccess}
            />
          ) : step === "recoverPin" ? (
            <PinSetupStep
              busy={busy}
              confirmPin={confirmPin}
              errors={formErrors}
              formError={formError}
              messages={messages}
              onConfirmPinChange={(value) => {
                setConfirmPin(value.slice(0, 72));
                clearFieldError("confirmPin");
              }}
              onPinChange={(value) => {
                setPin(value.slice(0, 72));
                clearFieldError("pin");
              }}
              onSubmit={completeRecoverPINReset}
              pin={pin}
              subtitle="Choose a new password for this account."
              title="Reset your password"
              submitLabel="Save new password"
            />
          ) : step === "shopSetup" ? (
            <ShopSetupStep
              busy={busy}
              country={country}
              errors={formErrors}
              formError={formError}
              industry={industry}
              isReferralVisible={isReferralVisible}
              leadTimeDays={leadTimeDays}
              messages={messages}
              onLeadTimeChange={(value) => {
                setLeadTimeDays(normalizeLeadTime(value));
                clearFieldError("leadTimeDays");
              }}
              onPhoneChange={(value) => {
                setPhone(value);
                clearFieldError("phone");
              }}
              onCountryChange={(nextCountry) => {
                countrySelectionTouched.current = true;
                setCountrySelectionSource("manual");
                setCountry(nextCountry);
              }}
              onShopNameChange={(value) => {
                setShopName(value);
                clearFieldError("shopName");
              }}
              onReferralCodeChange={(value) => setReferralCode(value.replace(/[^a-z0-9]/gi, "").slice(0, 16).toUpperCase())}
              onShowReferral={() => setIsReferralVisible(true)}
              onSubmit={continueToBranchSetup}
              phone={phone}
              referralCode={referralCode}
              shopName={shopName}
            />
          ) : (
            <BranchSetupStep
              branchName={branchName}
              branches={branches}
              busy={busy}
              errors={formErrors}
              formError={formError}
              leadTimeDays={leadTimeDays}
              messages={messages}
              onAddBranch={addBranch}
              onBranchNameChange={(value) => {
                setBranchName(value);
                clearFieldError("branchName");
              }}
              onRemoveBranch={(branchId) => setBranches((current) => current.filter((branch) => branch.id !== branchId))}
              onSubmit={completeBranchSetup}
              shopName={shopName}
            />
          )}
        </div>
      </main>
    </div>
  );
}

function EmailStep({
  busy,
  canSubmitEmail,
  copy,
  error,
  industry,
  industryOptions,
  mode,
  messages,
  needsIndustry,
  onIndustryChange,
  onModeChange,
  onEmailChange,
  onSubmit,
  email,
  password,
  confirmPassword,
  onPasswordChange,
  onConfirmPasswordChange,
}) {
  return (
    <form onSubmit={onSubmit} className={`fade-up min-w-0 ${needsIndustry ? "auth-registration-form" : ""}`}>
      <div className="flex w-full max-w-sm rounded-lg border border-border bg-card p-1 sm:inline-flex sm:w-auto">
        {["login", "register", "recover"].map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => onModeChange(item)}
            className={`min-w-0 flex-1 rounded-md px-2 py-2 text-xs font-semibold capitalize transition-colors sm:flex-none sm:px-3 ${
              mode === item ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {messages.auth.modeSwitch[item]}
          </button>
        ))}
      </div>

      <div className="mt-6 sm:mt-7">
        <h2 className="text-[26px] font-semibold leading-[1.08] tracking-[-0.02em] sm:text-[28px]">
          {copy.title}
        </h2>
      </div>

      <div className="mt-4 sm:mt-5">
        <div className={needsIndustry ? "auth-registration-fields max-w-sm" : "max-w-sm space-y-5"}>
          <AuthField label={mode === "register" ? "Email address" : "Shop or account email"}>
            <AuthInput
              icon={Mail}
              value={email}
              onChange={(event) => onEmailChange(event.target.value)}
              autoComplete="email"
              autoFocus
              inputMode="email"
              placeholder="you@example.com"
              type="email"
              data-testid="auth-email-input"
            />
          </AuthField>
          {mode === "register" || mode === "login" ? (
            <AuthField label={mode === "register" ? "Create password" : "Password"}>
              <AuthInput
                icon={KeyRound}
                value={password}
                onChange={(event) => onPasswordChange(event.target.value)}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                maxLength={72}
                placeholder="8–72 characters"
                type="password"
                data-testid="auth-registration-password-input"
              />
            </AuthField>
          ) : null}
          {mode === "register" ? (
            <AuthField label="Confirm password">
              <AuthInput
                icon={ShieldCheck}
                value={confirmPassword}
                onChange={(event) => onConfirmPasswordChange(event.target.value)}
                autoComplete="new-password"
                maxLength={72}
                placeholder="Repeat your password"
                type="password"
                data-testid="auth-registration-confirm-password-input"
              />
            </AuthField>
          ) : null}
          {needsIndustry ? (
            <AuthField label={messages.auth.fields.industry}>
              <FormSelect
                icon={Shapes}
                value={industry}
                onValueChange={onIndustryChange}
                placeholder={messages.auth.placeholders.selectIndustry}
                options={industryOptions}
                contentClassName="max-h-80 rounded-2xl p-2"
                itemClassName="my-1 rounded-xl border border-border bg-muted px-3 py-3 data-[state=checked]:border-primary data-[state=checked]:bg-accent data-[state=checked]:text-primary"
                testId="auth-industry-select"
              />
            </AuthField>
          ) : null}
        </div>
        {mode === "login" ? (
          <button
            type="button"
            data-testid="login-forgot-password-link"
            onClick={() => onModeChange("recover")}
            className="mt-3 text-sm font-medium text-primary hover:underline"
          >
            Forgot password?
          </button>
        ) : null}
      </div>

      {error ? (
        <div className="auth-entry-feedback max-w-sm">
          <Guidance error={error} fallback="" />
        </div>
      ) : null}

      <Button
        data-testid="auth-email-continue"
        type="submit"
        size="lg"
        disabled={!canSubmitEmail || busy}
        className="auth-registration-submit mt-6 h-11 w-full max-w-sm sm:mt-7"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{mode === "login" ? "Sign in" : copy.action}<ArrowRight className="h-4 w-4 ml-1" /></>}
      </Button>
    </form>
  );
}

function VerifyStep({
  busy,
  code,
  error,
  isLoginPIN,
  loginStep,
  messages,
  mode,
  normalizedEmail,
  onCodeChange,
  onRegisterInstead,
  onResend,
  onSubmit,
  resendCount,
  showRegisterPrompt,
}) {
  return (
    <div className="fade-up min-w-0">
      <div className="text-[10px] font-medium uppercase tracking-[0.24em] text-muted-foreground mb-4">
        {isLoginPIN ? messages.auth.verification.secureSignIn : loginStep === "otp_setup" ? messages.auth.verification.staffSetup : messages.auth.verification.verification}
      </div>
      <h2 className="text-[28px] font-semibold leading-[1.08] tracking-[-0.02em] sm:text-3xl lg:text-[34px]">
        {isLoginPIN ? "Enter your password" : messages.auth.verification.enterCode}
      </h2>
      <p className="mt-3 text-muted-foreground text-[15px]">
        {isLoginPIN ? "Use the password for this Shopbook account." : messages.auth.verification.enterCodeHint}
      </p>
      <div className="mt-4 inline-flex max-w-full break-all rounded-full border border-[#d8e5df] bg-[#f7fbf8] px-4 py-2 text-[12px] font-medium text-[#1f4d3b]">
        {normalizedEmail}
      </div>

      <div className="mt-7 w-full max-w-sm sm:mt-10">
        {isLoginPIN ? (
          <AuthInput
            data-testid="auth-password-input"
            value={code}
            onChange={(event) => onCodeChange(event.target.value)}
            autoComplete="current-password"
            autoFocus
            icon={KeyRound}
            maxLength={72}
            placeholder="Password"
            type="password"
          />
        ) : (
          <AuthOtpInput
            data-testid="auth-code-input"
            value={code}
            onChange={onCodeChange}
            autoFocus
          />
        )}
      </div>

      <div className="max-w-sm">
        <Guidance
          error={error}
          fallback={isLoginPIN ? (code.length >= 8 ? "Password ready." : "Enter your password to continue.") : code.length === 4 ? messages.auth.verification.codeCapturedReady : messages.auth.verification.enterCodeFallback}
        />
      </div>

      <Button onClick={onSubmit} disabled={busy} size="lg" className="mt-6 h-11 w-full max-w-sm sm:mt-8">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isLoginPIN ? messages.auth.screen.signIn : messages.auth.verification.verifyAndContinue}
      </Button>

      {showRegisterPrompt ? (
        <Button type="button" variant="outline" onClick={onRegisterInstead} className="mt-3 h-11 w-full max-w-sm">
          {messages.auth.verification.createAccountInstead}
        </Button>
      ) : null}

      {!isLoginPIN ? (
        <div className="mt-5 flex max-w-sm flex-col items-start gap-2 rounded-lg bg-muted/50 px-4 py-3 text-sm text-muted-foreground min-[420px]:flex-row min-[420px]:items-center min-[420px]:justify-between sm:mt-6">
          <span>{messages.auth.verification.noCodeYet}</span>
          <button type="button" onClick={onResend} disabled={busy || isLoginPIN} className="font-medium text-foreground hover:text-primary disabled:opacity-50">
            {messages.auth.verification.resend(resendCount)}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function StaffShopStep({ busy, error, messages, onSelect, onSubmit, options, email, selectedShopId }) {
  return (
    <div className="fade-up min-w-0">
      <Eyebrow title={messages.auth.screen.staffAccess} heading={messages.auth.screen.chooseYourShop} helper={messages.auth.screen.chooseYourShopHelper} />
      <div className="mt-4 break-all rounded-lg bg-muted/50 px-4 py-3 text-sm font-medium">{email}</div>
      <div className="mt-5 space-y-3">
        {options.map((option) => {
          const selected = option.shopId === selectedShopId;
          return (
            <button
              key={option.shopId}
              type="button"
              onClick={() => onSelect(option.shopId)}
              className={`w-full rounded-lg border p-3 text-left transition-colors sm:p-4 ${selected ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">
                  {(option.shopName?.trim()?.[0] || option.branchName?.trim()?.[0] || messages.settings.shopName?.[0] || messages.common.unknown?.[0] || "S").toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="break-words font-semibold">{option.shopName}</div>
                  <div className="mt-1 break-words text-xs text-muted-foreground">{option.branchName} - {formatRoleLabel(option.role, messages)}</div>
                </div>
                {selected ? <Check className="h-4 w-4 text-primary" /> : null}
              </div>
            </button>
          );
        })}
      </div>
      <Guidance error={error} fallback={selectedShopId ? messages.auth.status.shopSelectedContinue : messages.auth.status.selectShopToContinue} />
      <Button onClick={onSubmit} disabled={busy || !selectedShopId} size="lg" className="mt-6 w-full h-11">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : messages.auth.screen.continue}
      </Button>
    </div>
  );
}

function PinSetupStep({ busy, confirmPin, errors, formError, messages, onConfirmPinChange, onPinChange, onSubmit, pin, subtitle, title, submitLabel }) {
  return (
    <div className="fade-up min-w-0">
      <Eyebrow title="Account password" heading={title} helper={subtitle} />
      <div className="mt-5 max-w-sm space-y-3.5">
        <AuthField label="New password" error={errors.pin}>
          <AuthInput value={pin} onChange={(event) => onPinChange(event.target.value)} type="password" autoComplete="new-password" maxLength={72} placeholder="At least 8 characters" />
        </AuthField>
        <AuthField label="Confirm password" error={errors.confirmPin}>
          <AuthInput value={confirmPin} onChange={(event) => onConfirmPinChange(event.target.value)} type="password" autoComplete="new-password" maxLength={72} placeholder="Repeat your password" />
        </AuthField>
      </div>
      {formError ? <div className="max-w-sm"><Guidance error={formError} fallback="" /></div> : null}
      <Button onClick={onSubmit} disabled={busy} size="lg" className="mt-5 h-11 w-full max-w-sm">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel}
      </Button>
    </div>
  );
}

function ShopSetupStep({
  busy,
  country,
  errors,
  formError,
  industry,
  isReferralVisible,
  leadTimeDays,
  messages,
  onCountryChange,
  onLeadTimeChange,
  onPhoneChange,
  onReferralCodeChange,
  onShopNameChange,
  onShowReferral,
  onSubmit,
  phone,
  referralCode,
  shopName,
}) {
  const { language } = useLocalization();
  const extras = REGISTRATION_EXTRAS[language] || REGISTRATION_EXTRAS.en;

  return (
    <div className="fade-up min-w-0">
      {industry ? (
        <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3.5 py-1.5 text-xs font-semibold text-foreground/80">
          <Sparkles className="h-3.5 w-3.5" />
          {industry}
        </div>
      ) : null}
      <div className="mt-5 max-w-sm space-y-5 sm:mt-6">
        <AuthField label={messages.auth.fields.shopName} error={errors.shopName}>
          <AuthInput
            icon={Building2}
            value={shopName}
            onChange={(event) => onShopNameChange(event.target.value)}
            placeholder={messages.auth.placeholders.shopName}
          />
        </AuthField>
        <AuthField label="Business phone number" error={errors.phone}>
          <PhoneNumberField
            country={country}
            countries={AFRICAN_COUNTRIES}
            onCountryChange={onCountryChange}
            value={phone}
            onChange={(event) => onPhoneChange(event.target.value)}
            testId="auth-registration-phone-input"
          />
        </AuthField>
        <AuthField label={messages.auth.fields.supplierLeadTime} error={errors.leadTimeDays}>
          <div className="relative">
            <AuthInput icon={Truck} className="pr-14" value={leadTimeDays} onChange={(event) => onLeadTimeChange(event.target.value)} inputMode="numeric" placeholder={messages.auth.placeholders.leadTimeDays} />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground">{extras.days}</span>
          </div>
        </AuthField>
        {isReferralVisible || referralCode ? (
          <AuthField label={extras.referral}>
            <AuthInput icon={Tag} className="uppercase" value={referralCode} onChange={(event) => onReferralCodeChange(event.target.value)} maxLength={16} placeholder={extras.referralPlaceholder} />
          </AuthField>
        ) : (
          <button type="button" onClick={onShowReferral} className="justify-self-start px-1 py-2 text-sm font-medium text-primary hover:underline">
            + {extras.referral}
          </button>
        )}
      </div>

      {formError ? <div className="max-w-sm"><Guidance error={formError} fallback="" /></div> : null}
      <Button onClick={onSubmit} disabled={busy} size="lg" className="mt-6 h-11 w-full max-w-sm sm:mt-7">
        {messages.auth.screen.continue} <ArrowRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
  );
}

function BranchSetupStep({ branchName, branches, busy, errors, formError, leadTimeDays, messages, onAddBranch, onBranchNameChange, onRemoveBranch, onSubmit, shopName }) {
  return (
    <div className="fade-up min-w-0">
      <div className="text-[10px] font-medium uppercase tracking-[0.24em] text-muted-foreground mb-4">
        {messages.auth.screen.branchSetup}
      </div>
      <h2 className="text-2xl font-semibold leading-[1.08] tracking-[-0.02em] sm:text-[28px]">
        {messages.auth.screen.addYourBranch}
      </h2>
      <p className="mt-3 text-[15px] text-muted-foreground">
        {messages.auth.screen.branchSetupHelper}
      </p>

      <div className="mt-5 rounded-lg border border-border-strong/70 bg-[#F4F5F3] px-4 py-3">
        <div className="font-semibold">{shopName}</div>
        <div className="mt-1 text-xs text-foreground/65">
          {messages.auth.screen.supplierLeadTime(Number(leadTimeDays || "1"))}
        </div>
      </div>

      {branches.length ? (
        <div className="mt-4 max-h-[32dvh] space-y-2 overflow-y-auto pr-1 sm:max-h-none sm:overflow-visible sm:pr-0">
          {branches.map((branch, index) => (
            <div key={branch.id} className="flex min-w-0 items-center gap-3 rounded-xl border border-primary/45 bg-muted px-3 py-3 sm:px-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">{index + 1}</div>
              <div className="min-w-0 flex-1">
                <div className="break-words font-medium">{branch.name}</div>
                <div className="text-xs text-muted-foreground">{index === 0 ? messages.auth.screen.defaultBranch : messages.auth.screen.branch}</div>
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => onRemoveBranch(branch.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="mt-6">
        <AuthField label={messages.auth.fields.branchName} error={errors.branchName}>
          <div className="flex gap-2">
            <AuthInput icon={Building2} value={branchName} onChange={(event) => onBranchNameChange(event.target.value)} placeholder={messages.auth.placeholders.branchName} />
            <Button type="button" variant="outline" size="icon" onClick={onAddBranch} className="h-12 w-12 shrink-0 rounded-xl border-primary/45 bg-muted shadow-none">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </AuthField>
      </div>

      {formError ? <Guidance error={formError} fallback="" /> : null}
      <Button onClick={onSubmit} disabled={busy} size="lg" className="mt-7 h-11 w-full">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : messages.auth.screen.createAccount}
      </Button>
    </div>
  );
}

function Eyebrow({ title, heading, helper }) {
  return (
    <div>
      <div className="text-[10px] font-medium uppercase tracking-[0.24em] text-muted-foreground mb-3">{title}</div>
      <h2 className="text-[28px] font-semibold leading-[1.08] sm:text-3xl">{heading}</h2>
      <p className="mt-2 text-muted-foreground text-sm">{helper}</p>
    </div>
  );
}

function Guidance({ error, fallback }) {
  const Icon = error ? AlertCircle : Info;

  return (
    <div
      role={error ? "alert" : "status"}
      className={`mt-4 flex items-start gap-2.5 rounded-xl border px-3.5 py-3 text-xs font-medium leading-5 shadow-sm ${
        error
          ? "border-destructive/30 bg-destructive/10 text-destructive"
          : "border-primary/20 bg-primary/[0.075] text-foreground/80 dark:border-primary/25 dark:bg-primary/[0.11] dark:text-foreground"
      }`}
    >
      <span
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
          error ? "bg-destructive/15" : "bg-primary/15 text-primary"
        }`}
      >
        <Icon className="h-3 w-3" aria-hidden="true" />
      </span>
      <span className="min-w-0 break-words">{error || fallback}</span>
    </div>
  );
}

function Card({ k, l }) {
  return (
    <div className="auth-brand-card rounded-lg border border-white/10 bg-white/[0.04] p-3.5 backdrop-blur-sm">
      <div className="text-sm font-semibold text-white">{k}</div>
      <div className="text-[11px] uppercase tracking-wider text-white/50 mt-0.5">{l}</div>
    </div>
  );
}
